import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import {
  documentUploadsCreate,
  documentUploadsOnboardingConsultantGroupsList,
  documentUploadsOnboardingPortfoliosList,
} from '~/data/generated/endpoints/document-uploads/document-uploads';
import { portfoliosList } from '~/data/generated/endpoints/portfolios/portfolios';
import { serviceProvidersList } from '~/data/generated/endpoints/service-providers/service-providers';
import {
  DocumentUploadsOnboardingConsultantGroupsListResponse,
  DocumentUploadsOnboardingPortfoliosListResponse,
} from '~/data/generated/zod/document-uploads/document-uploads.zod';
import { ServiceProvidersListResponse } from '~/data/generated/zod/service-providers/service-providers.zod';
import { parseApiResponse } from '~/data/parseApiResponse';
import { DocumentTypeHint, ProcessedDocumentClassification } from '~/data/schemas/documentReview';
import { portfolioOptionSchema } from '~/data/schemas/portfolio';
import { uploadAttachment } from '~/data/uploadAttachment';

export type DialogStep = 'select' | 'uploading';

/**
 * Onboarding/acquisition intake mode (AMP-518). Empty string is the ordinary
 * upload; the other two create records (properties, cars, and for
 * ``new_portfolio`` the portfolio itself) from the uploaded contract.
 */
export type IntakeTarget = '' | 'existing_portfolio' | 'new_portfolio';

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface FileEntry {
  file: File;
  status: FileUploadStatus;
  error?: string;
  /**
   * When true, keep the whole PDF as one document: the pipeline skips splitting
   * but still classifies + extracts the file normally (BE
   * ``document_type_hint = single``). Off by default, so a multi-document batch
   * PDF is run through the splitter (``document_type_hint = auto``) and each
   * document is separated for review; tick it for a file the user knows is a
   * single document the splitter would otherwise break into pieces.
   */
  keepWhole: boolean;
}

interface PortfolioOption {
  id: string;
  name: string;
}

/** Classifications the pipeline can auto-approve against (real extraction support). */
export const EXPECTED_TYPE_OPTIONS = [
  ProcessedDocumentClassification.Invoice,
  ProcessedDocumentClassification.Ticket,
] as const;

/** Max PDF size (MB) accepted by every AI-pipeline upload surface. */
export const MAX_FILE_SIZE_MB = 75;

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

interface UseDocumentUploadArgs {
  /** Load portfolios + seed initial files only while the host surface is open. */
  open: boolean;
  initialFiles?: File[];
  /**
   * Enable the onboarding/acquisition intake controls (AMP-518). Opt-in so the
   * consultant-facing Add-to-Property tab neither renders them nor fetches the
   * staff-only selector endpoints behind them.
   */
  onboarding?: boolean;
}

/**
 * State + submit logic for the AI document-processing upload (per-file split
 * choice, portfolio + auto-approval config, and the actual attachment upload +
 * DocumentUpload creation).
 *
 * Extracted from DocumentUploadDialog so the same behavior backs both the
 * standalone dialog and the "AI pipeline" tab of the Add-to-Property dialog.
 * Each uploaded file becomes its own DocumentUpload, so the split choice
 * (``keepWhole``) lives per file; portfolio + auto-approval apply to the batch.
 */
export function useDocumentUpload({
  open,
  initialFiles,
  onboarding = false,
}: UseDocumentUploadArgs) {
  const [dialogStep, setDialogStep] = useState<DialogStep>('select');
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const [portfolios, setPortfolios] = useState<PortfolioOption[]>([]);
  const [portfolio, setPortfolio] = useState<string>('');
  const [expectedTypes, setExpectedTypes] = useState<string[]>([]);
  const [autoApprove, setAutoApprove] = useState<boolean>(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Seed `initialFiles` only once per open so a later change to the prop
  // reference doesn't wipe files the user has since added, removed, or toggled.
  const seededForOpenRef = useRef(false);

  // Onboarding/acquisition intake (AMP-518); inert unless `onboarding` is set.
  const [intakeTarget, setIntakeTarget] = useState<IntakeTarget>('');
  const [consultantGroups, setConsultantGroups] = useState<PortfolioOption[]>([]);
  const [onboardingConsultantGroup, setOnboardingConsultantGroup] = useState<string>('');
  const [onboardingPortfolios, setOnboardingPortfolios] = useState<PortfolioOption[]>([]);
  const [onboardingPortfolio, setOnboardingPortfolio] = useState<string>('');
  const [allowRecordCreation, setAllowRecordCreation] = useState<boolean>(false);
  const [onboardingPortfolioName, setOnboardingPortfolioName] = useState<string>('');
  const [onboardingClientName, setOnboardingClientName] = useState<string>('');
  const [serviceProviders, setServiceProviders] = useState<PortfolioOption[]>([]);
  const [onboardingServiceProvider, setOnboardingServiceProvider] = useState<string>('');

  // Either intake target creates records, so both lock the upload to a contract
  // and drop the auto-split / auto-approval config that doesn't apply.
  const isCreationMode = intakeTarget === 'existing_portfolio' || intakeTarget === 'new_portfolio';

  // Auto-approval needs a portfolio to scope candidate matching and at least one
  // expected type to act on (the BE rejects auto_approve without both).
  const autoApproveDisabled = isCreationMode || !portfolio || expectedTypes.length === 0;
  const effectiveAutoApprove = autoApproveDisabled ? false : autoApprove;

  // Required onboarding fields per intake target, checked here so the user isn't
  // sent to a server-only failure:
  //   existing_portfolio → consultant group + a selected portfolio
  //   new_portfolio      → consultant group + a portfolio name
  const onboardingFieldsValid =
    intakeTarget === 'existing_portfolio'
      ? Boolean(onboardingConsultantGroup) && Boolean(onboardingPortfolio)
      : intakeTarget === 'new_portfolio'
        ? Boolean(onboardingConsultantGroup) && Boolean(onboardingPortfolioName.trim())
        : true;

  const isUploadDisabled = fileEntries.length === 0 || !onboardingFieldsValid;

  const successCount = fileEntries.filter((e) => e.status === 'success').length;
  const errorCount = fileEntries.filter((e) => e.status === 'error').length;

  // Seed the file list from `initialFiles` on the first open, not on every
  // change to the prop: re-seeding while the dialog is open would clobber the
  // user's added/removed/toggled entries.
  useEffect(() => {
    if (!open) {
      seededForOpenRef.current = false;
      return;
    }
    if (seededForOpenRef.current) return;
    if (initialFiles && initialFiles.length > 0) {
      seededForOpenRef.current = true;
      setFileEntries(initialFiles.map((file) => ({ file, status: 'pending', keepWhole: false })));
    }
  }, [open, initialFiles]);

  // Load portfolios when the host opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    portfoliosList()
      .then(({ data }) => {
        if (!active) return;
        const parsed = parseApiResponse(z.array(portfolioOptionSchema), data, 'Portfolio');
        setPortfolios(parsed.map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {
        // Surface the failure as an empty selector rather than an unhandled
        // rejection; the auto-approve toggle stays disabled with no portfolio.
        if (active) setPortfolios([]);
      });
    return () => {
      active = false;
    };
  }, [open]);

  // Load the onboarding selectors (staff-only endpoints) when the host opens.
  useEffect(() => {
    if (!open || !onboarding) return;
    let active = true;
    documentUploadsOnboardingConsultantGroupsList()
      .then(({ data }) => {
        if (!active) return;
        setConsultantGroups(
          parseApiResponse(
            DocumentUploadsOnboardingConsultantGroupsListResponse,
            data,
            'OnboardingConsultantGroups',
          ),
        );
      })
      .catch(() => {
        if (active) setConsultantGroups([]);
      });
    return () => {
      active = false;
    };
  }, [open, onboarding]);

  useEffect(() => {
    if (!open || !onboarding) return;
    let active = true;
    serviceProvidersList()
      .then(({ data }) => {
        if (!active) return;
        const parsed = parseApiResponse(ServiceProvidersListResponse, data, 'ServiceProviders');
        setServiceProviders(parsed.map((sp) => ({ id: sp.id, name: sp.name })));
      })
      .catch(() => {
        if (active) setServiceProviders([]);
      });
    return () => {
      active = false;
    };
  }, [open, onboarding]);

  // Portfolios inside the selected consultant group (the existing-portfolio target).
  useEffect(() => {
    if (!onboarding || !onboardingConsultantGroup) {
      setOnboardingPortfolios([]);
      setOnboardingPortfolio('');
      return;
    }
    let active = true;
    documentUploadsOnboardingPortfoliosList({ consultant_group: onboardingConsultantGroup })
      .then(({ data }) => {
        if (!active) return;
        setOnboardingPortfolios(
          parseApiResponse(
            DocumentUploadsOnboardingPortfoliosListResponse,
            data,
            'OnboardingPortfolios',
          ),
        );
      })
      .catch(() => {
        if (active) setOnboardingPortfolios([]);
      });
    return () => {
      active = false;
    };
  }, [onboarding, onboardingConsultantGroup]);

  const resetState = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setDialogStep('select');
    setFileEntries([]);
    setPortfolio('');
    setExpectedTypes([]);
    setAutoApprove(false);
    setCurrentUploadIndex(0);
    setUploadComplete(false);
    setIntakeTarget('');
    setOnboardingConsultantGroup('');
    setOnboardingPortfolios([]);
    setOnboardingPortfolio('');
    setAllowRecordCreation(false);
    setOnboardingPortfolioName('');
    setOnboardingClientName('');
    setOnboardingServiceProvider('');
  }, []);

  /** Switch consultant group; the portfolio list it scoped is no longer valid. */
  const handleConsultantGroupChange = useCallback((value: string) => {
    setOnboardingConsultantGroup(value);
    setOnboardingPortfolio('');
    // Clear the options too, not just the selection. They were scoped to the
    // previous group and stay selectable until the refetch lands, so a user can
    // pick a portfolio the new group does not cover and submit it.
    setOnboardingPortfolios([]);
  }, []);

  /** Switch intake target, clearing every field that doesn't survive the change. */
  const handleIntakeTargetChange = useCallback((value: IntakeTarget) => {
    setIntakeTarget(value);
    setOnboardingPortfolio('');
    setAllowRecordCreation(false);
    setOnboardingPortfolioName('');
    setOnboardingClientName('');
    setOnboardingServiceProvider('');
    // The auto-split config doesn't apply once we're creating records.
    if (value !== '') {
      setPortfolio('');
      setExpectedTypes([]);
      setAutoApprove(false);
    }
  }, []);

  const toggleExpectedType = useCallback(
    (type: string) =>
      setExpectedTypes((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
      ),
    [],
  );

  const handleFilesSelected = useCallback((files: File[]) => {
    setFileEntries((prev) => [
      ...prev,
      ...files.map((file) => ({ file, status: 'pending' as const, keepWhole: false })),
    ]);
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFileEntries((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const toggleFileKeepWhole = useCallback((index: number) => {
    setFileEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, keepWhole: !entry.keepWhole } : entry)),
    );
  }, []);

  const setAllKeepWhole = useCallback((value: boolean) => {
    setFileEntries((prev) => prev.map((entry) => ({ ...entry, keepWhole: value })));
  }, []);

  const handleUpload = useCallback(async () => {
    if (fileEntries.length === 0) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setDialogStep('uploading');
    setCurrentUploadIndex(0);
    setUploadComplete(false);

    const entries = [...fileEntries];

    for (let i = 0; i < entries.length; i++) {
      if (controller.signal.aborted) return;

      setCurrentUploadIndex(i);
      setFileEntries((prev) =>
        prev.map((entry, idx) => (idx === i ? { ...entry, status: 'uploading' } : entry)),
      );

      // A "keep whole" file skips splitting (BE hint ``single``). Auto-approval
      // still applies — its type is detected normally — but there are no split
      // boundaries to review.
      const keepWhole = entries[i].keepWhole;

      try {
        // Step 1: Upload the file to get an attachment. Pass the abort signal so
        // cancelling (Cancel/backdrop-close) actually stops the in-flight request
        // rather than letting it finish and create an orphaned DocumentUpload.
        const attachment = await uploadAttachment(
          {
            file: entries[i].file,
            name: entries[i].file.name,
            fileType: 'pdf',
          },
          { signal: controller.signal },
        );

        if (controller.signal.aborted) return;

        // Step 2: Create a DocumentUpload
        await documentUploadsCreate(
          {
            originalAttachment: attachment.id,
            // A creation-mode upload is always a contract, and contracts skip
            // splitting outright, so the per-file keep-whole choice doesn't apply.
            documentTypeHint: isCreationMode
              ? DocumentTypeHint.Contract
              : keepWhole
                ? DocumentTypeHint.Single
                : DocumentTypeHint.Auto,
            portfolio: isCreationMode
              ? intakeTarget === 'existing_portfolio'
                ? onboardingPortfolio || undefined
                : undefined
              : portfolio || undefined,
            autoApprove: effectiveAutoApprove,
            expectedDocumentTypes: isCreationMode ? undefined : expectedTypes,
            intakeTarget: intakeTarget || undefined,
            // new_portfolio always creates records (there is nothing to match
            // against); the backend enforces this too, this keeps the request honest.
            allowRecordCreation:
              intakeTarget === 'new_portfolio'
                ? true
                : intakeTarget === 'existing_portfolio'
                  ? allowRecordCreation
                  : undefined,
            onboardingConsultantGroup: isCreationMode
              ? onboardingConsultantGroup || undefined
              : undefined,
            onboardingPortfolioName:
              intakeTarget === 'new_portfolio' ? onboardingPortfolioName || undefined : undefined,
            onboardingClientName:
              intakeTarget === 'new_portfolio' ? onboardingClientName || undefined : undefined,
            onboardingServiceProvider:
              intakeTarget === 'new_portfolio' ? onboardingServiceProvider || undefined : undefined,
          },
          { signal: controller.signal },
        );

        if (controller.signal.aborted) return;

        setFileEntries((prev) =>
          prev.map((entry, idx) => (idx === i ? { ...entry, status: 'success' } : entry)),
        );
      } catch (err) {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : 'Failed to upload';
        setFileEntries((prev) =>
          prev.map((entry, idx) =>
            idx === i ? { ...entry, status: 'error', error: message } : entry,
          ),
        );
      }
    }

    if (!controller.signal.aborted) {
      setUploadComplete(true);
    }
  }, [
    fileEntries,
    portfolio,
    expectedTypes,
    effectiveAutoApprove,
    isCreationMode,
    intakeTarget,
    onboardingConsultantGroup,
    onboardingPortfolio,
    allowRecordCreation,
    onboardingPortfolioName,
    onboardingClientName,
    onboardingServiceProvider,
  ]);

  return {
    dialogStep,
    fileEntries,
    portfolios,
    portfolio,
    setPortfolio,
    expectedTypes,
    setExpectedTypes,
    toggleExpectedType,
    autoApprove,
    setAutoApprove,
    effectiveAutoApprove,
    autoApproveDisabled,
    currentUploadIndex,
    uploadComplete,
    successCount,
    errorCount,
    handleFilesSelected,
    handleRemoveFile,
    toggleFileKeepWhole,
    setAllKeepWhole,
    handleUpload,
    resetState,
    isUploadDisabled,
    // Onboarding/acquisition intake (AMP-518)
    intakeTarget,
    handleIntakeTargetChange,
    isCreationMode,
    consultantGroups,
    onboardingConsultantGroup,
    handleConsultantGroupChange,
    onboardingPortfolios,
    onboardingPortfolio,
    setOnboardingPortfolio,
    allowRecordCreation,
    setAllowRecordCreation,
    onboardingPortfolioName,
    setOnboardingPortfolioName,
    onboardingClientName,
    setOnboardingClientName,
    serviceProviders,
    onboardingServiceProvider,
    setOnboardingServiceProvider,
  };
}

export type UseDocumentUpload = ReturnType<typeof useDocumentUpload>;
